import type { Grid, Position, Tile } from "@2048/game-core";

export interface ActuatorMetadata {
  score: number;
  bestScore: number;
  over: boolean;
  won: boolean;
  terminated: boolean;
}

declare global {
  interface Window {
    twttr?: { widgets: { load(): void } };
  }
}

function mustFind<T extends Element = HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}

/**
 * Renders the game to the DOM using CSS `tile-position-x-y` classes and the
 * original's double-`requestAnimationFrame` trick for move animations. Ported
 * from the original `HTMLActuator`; dead Google Analytics (`ga`) calls removed.
 */
export class HtmlActuator {
  private readonly tileContainer = mustFind(".tile-container");
  private readonly scoreContainer = mustFind(".score-container");
  private readonly bestContainer = mustFind(".best-container");
  private readonly messageContainer = mustFind(".game-message");
  private readonly sharingContainer = mustFind(".score-sharing");

  private score = 0;

  constructor(private readonly shouldDisplayAppendix: () => boolean) {}

  actuate(grid: Grid, metadata: ActuatorMetadata): void {
    window.requestAnimationFrame(() => {
      this.clearContainer(this.tileContainer);

      for (const column of grid.cells) {
        for (const cell of column) {
          if (cell) this.addTile(cell);
        }
      }

      this.updateScore(metadata.score);
      this.updateBestScore(metadata.bestScore);

      if (metadata.terminated) {
        if (metadata.over) {
          this.message(false); // You lose
        } else if (metadata.won) {
          this.message(true); // You win!
        }
      }
    });
  }

  /** Clears the win/lose message (used by both restart and keep-playing). */
  continueGame(): void {
    this.clearMessage();
  }

  private clearContainer(container: Element): void {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

  private addTile(tile: Tile): void {
    const wrapper = document.createElement("div");
    const inner = document.createElement("div");
    const position = tile.previousPosition ?? { x: tile.x, y: tile.y };
    const positionClass = this.positionClass(position);

    const classes = ["tile", `tile-${tile.value}`, positionClass];
    if (tile.value > 2048) classes.push("tile-super");

    this.applyClasses(wrapper, classes);

    inner.classList.add("tile-inner");
    inner.textContent = String(tile.value);

    if (tile.previousPosition) {
      // Render in the previous position first, then move on the next frame.
      window.requestAnimationFrame(() => {
        classes[2] = this.positionClass({ x: tile.x, y: tile.y });
        this.applyClasses(wrapper, classes);
      });
    } else if (tile.mergedFrom) {
      classes.push("tile-merged");
      this.applyClasses(wrapper, classes);

      // Render the tiles that merged.
      for (const merged of tile.mergedFrom) {
        this.addTile(merged);
      }
    } else {
      classes.push("tile-new");
      this.applyClasses(wrapper, classes);
    }

    wrapper.appendChild(inner);
    this.tileContainer.appendChild(wrapper);
  }

  private applyClasses(element: Element, classes: string[]): void {
    element.setAttribute("class", classes.join(" "));
  }

  private normalizePosition(position: Position): Position {
    return { x: position.x + 1, y: position.y + 1 };
  }

  private positionClass(position: Position): string {
    const normalized = this.normalizePosition(position);
    return `tile-position-${normalized.x}-${normalized.y}`;
  }

  private updateScore(score: number): void {
    this.clearContainer(this.scoreContainer);

    const difference = score - this.score;
    this.score = score;
    this.scoreContainer.textContent = String(this.score);

    if (difference > 0) {
      const addition = document.createElement("div");
      addition.classList.add("score-addition");
      addition.textContent = `+${difference}`;
      this.scoreContainer.appendChild(addition);
    }
  }

  private updateBestScore(bestScore: number): void {
    this.bestContainer.textContent = String(bestScore);
  }

  private message(won: boolean): void {
    const type = won ? "game-won" : "game-over";
    const message = won ? "You win!" : "Game over!";

    this.messageContainer.classList.add(type);
    const paragraph = this.messageContainer.getElementsByTagName("p")[0];
    if (paragraph) paragraph.textContent = message;

    this.clearContainer(this.sharingContainer);
    this.sharingContainer.appendChild(this.scoreTweetButton());

    const loadTwitterWidgets = (): void => {
      try {
        window.twttr?.widgets.load();
      } catch {
        setTimeout(loadTwitterWidgets, 1000);
      }
    };
    loadTwitterWidgets();

    if (this.shouldDisplayAppendix()) {
      this.messageContainer
        .querySelector(".message-appendix")
        ?.classList.add("visible");
    }
  }

  private clearMessage(): void {
    this.messageContainer.classList.remove("game-won");
    this.messageContainer.classList.remove("game-over");
  }

  private scoreTweetButton(): HTMLAnchorElement {
    const tweet = document.createElement("a");
    tweet.classList.add("twitter-share-button");
    tweet.setAttribute("href", "https://twitter.com/share");
    tweet.setAttribute("data-url", "http://2048.io");
    tweet.textContent = "Tweet";
    tweet.setAttribute(
      "data-text",
      `I scored ${this.score} points at 2048, a game where you join numbers to score high! #2048game`,
    );
    return tweet;
  }
}
